import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from './AuthContext.jsx';
import { useIdleLogout } from './useIdleLogout.js';
import abcLogo from '../assets/abc-logo.webp';

const GOOGLE_STORAGE_KEY = 'google_id_token';
const ACTIVE_PROVIDER_KEY = 'auth_active_provider';
const API_URL = import.meta.env.VITE_API_URL;

// Silent refresh calls Lambda's /auth/refresh this long before the current
// Google token expires so a fresh one is on hand before the old one dies
// mid-request. Refresh goes through our server (which holds the stored
// refresh_token) — no dependency on browser cookies to accounts.google.com,
// unlike the old One Tap silent-refresh path.
const GOOGLE_REFRESH_LEAD_MS = 5 * 60 * 1000;

// Floor between two refreshes. The silent-refresh effect depends on the decoded
// claims and replaces the credential, so a token that still looks expired after
// being refreshed re-triggers the effect immediately. When a client's clock runs
// ahead of Google's, EVERY fresh token looks expired and the app refreshes in a
// tight loop -- 143 calls in one minute on 2026-10-28 -- until one fails and the
// error path drops the credential and dumps the user back on the login gate.
// Hence the reports of logging in, being logged out, and logging in again.
const MIN_REFRESH_INTERVAL_MS = 30 * 1000;

// If a just-minted token STILL reads as expired, the local clock is wrong, not
// the token. decodeCredential already takes that view and trusts the server;
// the scheduler has to agree, or it spins. Check again after this instead.
const CLOCK_SKEW_RETRY_MS = 15 * 60 * 1000;

// Force-logout after this many minutes of no user activity. Independent of
// token lifetime — an active user with a fresh token still gets kicked out.
// Set to 0 (or negative) via env to disable.
const IDLE_TIMEOUT_MINUTES = Number.parseInt(
  import.meta.env.VITE_IDLE_TIMEOUT_MINUTES ?? '480',
  10
);
const IDLE_TIMEOUT_MS =
  Number.isFinite(IDLE_TIMEOUT_MINUTES) && IDLE_TIMEOUT_MINUTES > 0
    ? IDLE_TIMEOUT_MINUTES * 60 * 1000
    : 0;

// Optional client-side allow-list. Comma-separated emails; empty = allow all.
// This is UX, not real security — the tamper-resistant enforcement belongs at
// the Lambda layer (dispatcher rejects tokens whose email isn't in the list).
const ALLOWED_EMAILS = (import.meta.env.VITE_ALLOWED_EMAILS || '')
  .split(',')
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);
const HAS_ALLOWLIST = ALLOWED_EMAILS.length > 0;

function readStoredGoogleCredential() {
  try {
    const raw = sessionStorage.getItem(GOOGLE_STORAGE_KEY);
    if (!raw) return null;
    const { credential } = JSON.parse(raw);
    return credential || null;
  } catch {
    return null;
  }
}

function writeStoredGoogleCredential(credential) {
  try {
    sessionStorage.setItem(
      GOOGLE_STORAGE_KEY,
      JSON.stringify({ credential, storedAt: Date.now() })
    );
  } catch (err) {
    console.warn('[auth] sessionStorage.setItem failed — token in memory only:', err);
  }
}

// Exchange a Google auth-code (from the popup) for an id_token via Lambda.
// Lambda also stores the refresh_token server-side, keyed by user email.
async function exchangeAuthCodeForIdToken(code) {
  const res = await fetch(`${API_URL}/auth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: 'postmessage' }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Exchange failed: ${res.status} ${text}`);
  }
  const json = await res.json();
  if (!json.id_token) throw new Error('Exchange response missing id_token');
  return json.id_token;
}

// Request a fresh id_token using the server-stored refresh_token. Bearer is
// the possibly-stale current id_token — Lambda verifies its signature (not
// exp) to identify the user, then mints a new id_token from Google.
async function refreshIdTokenViaLambda(currentIdToken) {
  const res = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentIdToken}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Refresh failed: ${res.status} ${text}`);
  }
  const json = await res.json();
  if (!json.id_token) throw new Error('Refresh response missing id_token');
  return json.id_token;
}

// Decode a Google ID token. By default returns the claims even if `exp` is
// already past according to the local clock — some users have clocks drifted
// forward, and rejecting client-side would bounce them back to the login gate
// before the API ever sees the token. The server verifies exp with Google's
// JWKS on every request, so a real expiry surfaces as a 401 → SessionExpired
// modal, which is the correct UX. Pass `requireFuture` (used only for the
// sessionStorage rehydrate path) to skip a token we already know is stale.
function decodeCredential(credential, { requireFuture = false } = {}) {
  if (!credential) return null;
  try {
    const claims = jwtDecode(credential);
    if (!claims) return null;
    if (requireFuture && (!claims.exp || claims.exp * 1000 <= Date.now())) {
      console.info('[auth] Stored Google token is past exp locally — dropping');
      return null;
    }
    if (claims.exp && claims.exp * 1000 <= Date.now()) {
      const skewMinutes = Math.round((Date.now() - claims.exp * 1000) / 60000);
      console.warn(
        `[auth] Google token exp is ${skewMinutes}min behind local clock — trusting server to verify (likely clock skew)`
      );
    }
    return claims;
  } catch (err) {
    console.error('[auth] Failed to decode Google credential:', err);
    return null;
  }
}

// Renders the Google login gate and publishes normalized auth state into
// AuthContext. Must be rendered inside <GoogleOAuthProvider>.
//
// This was DualAdapter until 2026-10-28, when Auth0 was removed: its token
// verification checked only issuer and signature, accepting any audience in
// the tenant and any email whether verified or not. Roles are keyed on email,
// so that was an admin-impersonation path. Google is now the only provider.
export function GoogleAdapter({ children }) {

  // On page load: rehydrate whatever id_token is in sessionStorage. If it's
  // stale, keep it in state anyway — the effect below will call /auth/refresh
  // and swap in a fresh one. This makes F5 / tab-restore land back on the app
  // instead of the login gate as long as the server still has our
  // refresh_token.
  const [googleCredential, setGoogleCredential] = useState(readStoredGoogleCredential);

  // Lets the clock-skew path re-run the refresh effect later without changing
  // the credential, which is what would otherwise restart the loop.
  const [refreshTick, setRefreshTick] = useState(0);

  const googleClaims = useMemo(
    () => decodeCredential(googleCredential),
    [googleCredential]
  );
  const googleIsAuthenticated = Boolean(googleClaims);

  // Kept now that Google is the only provider: it still distinguishes "signed
  // in" from "signed out" across a reload, and a stale 'auth0' value left in
  // sessionStorage from before the removal must not be honored.
  const [activeProvider, setActiveProvider] = useState(() => {
    const stored = sessionStorage.getItem(ACTIVE_PROVIDER_KEY);
    return stored === 'google' ? stored : null;
  });

  useEffect(() => {
    if (activeProvider) {
      sessionStorage.setItem(ACTIVE_PROVIDER_KEY, activeProvider);
    } else {
      sessionStorage.removeItem(ACTIVE_PROVIDER_KEY);
    }
  }, [activeProvider]);

  // Latest Google credential in a ref so the async refresh callback can read
  // it without re-subscribing every state change.
  const googleCredentialRef = useRef(googleCredential);
  useEffect(() => { googleCredentialRef.current = googleCredential; }, [googleCredential]);

  // When the last refresh actually went out. A ref, not state: updating it must
  // not re-render, or it would retrigger the very effect it guards.
  const lastRefreshAtRef = useRef(0);

  // Silent refresh via Lambda. Two triggers:
  //   1. If the current id_token is already past exp (e.g. rehydrated from
  //      sessionStorage after a long pause), refresh immediately.
  //   2. Otherwise schedule a refresh ~5 min before exp.
  // Calls /auth/refresh with the current id_token as Bearer; Lambda verifies
  // its signature (ignoring exp), uses the stored refresh_token to mint a
  // new id_token from Google, returns it. On failure we drop the credential
  // and clear storage so the user lands on the login gate (refresh_token was
  // likely revoked). No dependency on browser cookies to accounts.google.com.
  useEffect(() => {
    if (!googleClaims?.exp) return undefined;
    const msUntilExpiry = googleClaims.exp * 1000 - Date.now();
    const doRefresh = async () => {
      const current = googleCredentialRef.current;
      if (!current) return;
      try {
        const fresh = await refreshIdTokenViaLambda(current);
        console.info('[auth] Google token refreshed via Lambda');
        setGoogleCredential(fresh);
        writeStoredGoogleCredential(fresh);
      } catch (err) {
        console.warn('[auth] /auth/refresh failed:', err.message);
        // Only nuke state if the token is actually past exp — a transient
        // network flake near exp shouldn't kick the user.
        if (msUntilExpiry <= 0) {
          setGoogleCredential(null);
          try { sessionStorage.removeItem(GOOGLE_STORAGE_KEY); } catch {}
        }
      }
    };
    if (msUntilExpiry <= 0) {
      const sinceLast = Date.now() - lastRefreshAtRef.current;
      if (sinceLast < MIN_REFRESH_INTERVAL_MS) {
        // We refreshed moments ago and the new token still reads as expired, so
        // the clock is wrong rather than the token. Stop hammering /auth/refresh
        // and look again later; the server verifies exp against Google on every
        // request, so a genuine expiry surfaces as a 401 and the session-expired
        // modal, which is the correct UX.
        console.warn(
          `[auth] Fresh token still past exp after ${Math.round(sinceLast / 1000)}s ` +
          '- treating as local clock skew, not refreshing again yet'
        );
        const skewTimer = setTimeout(() => setRefreshTick((n) => n + 1), CLOCK_SKEW_RETRY_MS);
        return () => clearTimeout(skewTimer);
      }
      lastRefreshAtRef.current = Date.now();
      doRefresh();
      return undefined;
    }
    const msUntilRefresh = Math.max(0, msUntilExpiry - GOOGLE_REFRESH_LEAD_MS);
    const refreshTimer = setTimeout(() => {
      lastRefreshAtRef.current = Date.now();
      doRefresh();
    }, msUntilRefresh);
    return () => {
      clearTimeout(refreshTimer);
    };
  }, [googleClaims, refreshTick]);

  // Avoids a stale 'google' pointer surviving logout.
  useEffect(() => {
    setActiveProvider(googleIsAuthenticated ? 'google' : null);
  }, [googleIsAuthenticated]);

  // Called after the auth-code popup returns a code AND Lambda exchanges it
  // for an id_token (see startGoogleLogin below). Sets the credential and
  // marks Google as the active provider.
  const applyGoogleIdToken = useCallback((idToken) => {
    const claims = decodeCredential(idToken);
    if (!claims) {
      console.error('[auth] id_token from Lambda could not be decoded');
      return;
    }
    console.info(
      `[auth] Google login success: ${claims.email} (exp ${new Date(claims.exp * 1000).toISOString()}, local now ${new Date().toISOString()})`
    );
    setGoogleCredential(idToken);
    setActiveProvider('google');
    writeStoredGoogleCredential(idToken);
  }, []);

  // Opens the Google popup, gets an auth-code, POSTs it to Lambda /auth/exchange
  // which returns an id_token and stores the refresh_token server-side.
  // Passing prompt: 'consent' + access_type: 'offline' the FIRST time is what
  // makes Google issue a refresh_token; subsequent logins can omit consent
  // (we'll always send them here to be safe — the UX is unchanged).
  const startGoogleLogin = useGoogleLogin({
    flow: 'auth-code',
    prompt: 'consent',
    access_type: 'offline',
    onSuccess: async ({ code }) => {
      try {
        const idToken = await exchangeAuthCodeForIdToken(code);
        applyGoogleIdToken(idToken);
      } catch (err) {
        console.error('[auth] /auth/exchange failed:', err.message);
      }
    },
    onError: (err) => {
      console.error('[auth] Google auth-code popup failed:', err);
    },
  });

  // Still async: apiClient awaits this, and it was async while Auth0 needed a
  // round-trip for its claims. Keeping the signature avoids touching callers.
  const getToken = useCallback(async () => {
    if (!decodeCredential(googleCredential)) return null;
    return googleCredential;
  }, [googleCredential]);

  const logout = useCallback(() => {
    // Clears browser state only. The refresh_token stored server-side by
    // /auth/exchange is NOT revoked here, so this does not end the session for
    // anyone holding the id_token -- see docs/backend/user-roles-spec.md.
    setGoogleCredential(null);
    try { sessionStorage.removeItem(GOOGLE_STORAGE_KEY); } catch {}
    googleLogout();
    setActiveProvider(null);
  }, []);

  const login = useCallback(() => {
    // No-op — the gate renders the Google button, which drives the flow.
  }, []);

  const providerAuthenticated = googleIsAuthenticated;

  // Always false. Auth0 was the only provider with an async startup (silent
  // auth on mount); the Google credential is read synchronously from
  // sessionStorage in the useState initializer above, so the first render
  // already knows whether we are signed in. Kept in the context shape because
  // consumers destructure it and the gate below reads it.
  const isLoading = false;

  // Kick the user back to the login gate after prolonged inactivity, even if
  // their token is still valid. Browser-side only: the API keeps accepting the
  // token until its own exp.
  useIdleLogout({
    timeoutMs: IDLE_TIMEOUT_MS,
    enabled: providerAuthenticated,
    onIdle: logout,
  });

  const user = useMemo(() => {
    if (!googleClaims) return null;
    return {
      sub: googleClaims.sub,
      email: googleClaims.email,
      name: googleClaims.name || googleClaims.email,
    };
  }, [googleClaims]);

  const provider = googleIsAuthenticated ? 'google' : null;

  const emailAllowed = !HAS_ALLOWLIST
    || Boolean(user?.email && ALLOWED_EMAILS.includes(user.email.toLowerCase()));
  // Downstream consumers see "not authenticated" if the email isn't allowed,
  // so ProtectedRoute keeps them out and apiClient sends no token.
  const isAuthenticated = providerAuthenticated && emailAllowed;

  const value = useMemo(
    () => ({ user, isAuthenticated, isLoading, getToken, login, logout, provider }),
    [user, isAuthenticated, isLoading, getToken, login, logout, provider]
  );

  const showRejection = providerAuthenticated && !emailAllowed;

  return (
    <AuthContext.Provider value={value}>
      {showRejection ? (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50">
          <div className="bg-white p-8 rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full text-center">
            <img
              src={abcLogo}
              alt="ABC Consulting"
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-xl font-semibold text-neutral-900 mb-2">
              Access denied
            </h1>
            <p className="text-sm text-neutral-600 mb-2">
              {user?.email
                ? <>Your account <strong>{user.email}</strong> isn't on the SW Operations Portal allow-list.</>
                : <>This account isn't on the SW Operations Portal allow-list.</>}
            </p>
            <p className="text-xs text-neutral-500 mb-6">
              Contact an administrator if you need access.
            </p>
            <button
              type="button"
              onClick={logout}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50"
            >
              Sign out
            </button>
          </div>
        </div>
      ) : isAuthenticated || isLoading ? (
        children
      ) : (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50">
          <div className="bg-white p-8 rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full text-center">
            <img
              src={abcLogo}
              alt="ABC Consulting"
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-xl font-semibold text-neutral-900 mb-2">
              SW Operations Portal
            </h1>
            <p className="text-sm text-neutral-600 mb-6">
              Sign in to continue.
            </p>
            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => startGoogleLogin()}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.257h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                Sign in with Google
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}
